import { queryOptions, useMutation } from "@tanstack/react-query"

import { apiKey } from "@/lib/api/changes"
import { apiFetch } from "@/lib/api/client"
import type {
  PortalAgreement,
  PortalTask,
  SignAgreementInput,
} from "@/types/portal"

export const portalQueries = {
  tasks: () =>
    queryOptions({
      ...portalTasks(),
      select: (portal) => portal.tasks,
    }),

  summary: () =>
    queryOptions({
      ...portalTasks(),
      select: (portal) => portal.summary,
    }),

  agreement: (firmId: string) =>
    queryOptions({
      queryKey: [...apiKey(`/firms/${firmId}/agreements`), "portal"],
      queryFn: async ({ signal }) => {
        const { data } = await apiFetch<{
          data: { current: PortalAgreement | null }
        }>(`/firms/${firmId}/agreements`, { signal })

        return data.current
      },
    }),
}

function portalTasks() {
  return queryOptions({
    queryKey: [...apiKey("/me/tasks"), "portal"],
    queryFn: async ({ signal }) => {
      const { data, meta } = await apiFetch<{
        data: PortalTask[]
        meta: { summary: string | null }
      }>("/me/tasks", { signal })

      return { tasks: data, summary: meta.summary }
    },
  })
}

export function useCompleteTask(taskId: string) {
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiFetch<{
        data: Pick<PortalTask, "id" | "key" | "status" | "closed_at">
      }>(`/tasks/${taskId}`, { method: "PATCH", body: { status: "closed" } })

      return data
    },
  })
}

export function useSignAgreement() {
  return useMutation({
    mutationFn: async ({
      agreementId,
      name,
      signature,
    }: SignAgreementInput) => {
      const { data } = await apiFetch<{ data: PortalAgreement }>(
        `/agreements/${agreementId}/signature`,
        { method: "POST", body: { name, signature, agreed: true } }
      )

      return data
    },
  })
}

export function useDeclineAgreement() {
  return useMutation({
    mutationFn: async ({
      agreementId,
      reason,
    }: {
      agreementId: string
      reason: string
    }) => {
      const { data } = await apiFetch<{ data: PortalAgreement }>(
        `/agreements/${agreementId}/decline`,
        { method: "POST", body: { reason } }
      )

      return data
    },
  })
}
